import { useEffect, useState } from "react";
import styles from "./Orders.module.css";
import { toast } from "react-toastify";
import { API } from "../../config";
import axios from "axios";
import Spin from "../Spinner/Spinner";
import { SearchInput } from "../ProductListing/Filters";
import { useNavigate } from "react-router-dom";
import { FiGrid } from "react-icons/fi";

const LIMIT = 10;

const filterOptions = [
  {
    label: "All",
    value: undefined,
  },
  {
    label: "Fulfilled",
    value: "fulfilled",
  },
  {
    label: "Draft",
    value: "cancelled",
  },
  {
    label: "Processing",
    value: "processing",
  },
];
export const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [filter, setFilter] = useState(undefined);

  const statusColors = {
    fulfilled: "#03ce00",
    cancelled: "#e12222",
    processing: "#ffa500",
    "active-cart": "#6687fa",
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatPrice = (amount) => {
    if (amount == null) return "-";
    const rounded = Math.round(amount);
    return `₹${rounded.toLocaleString("en-IN")}/-`;
  };

  const getOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/Admin/cartOrders`, {
        params: {
          page,
          limit: LIMIT,
          search: debouncedSearch || undefined,
          status: filter || undefined,
        },
      });
      setOrders(response.data.cartOrders || []);
      if (response.data.pagination?.totalPages) {
        setTotalPages(response.data.pagination.totalPages);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to get orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    getOrders();
  }, [page, debouncedSearch, filter]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  if (loading) {
    return <Spin />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Orders</h1>
        <div className={styles.searchContainer}>
          <SearchInput value={search} onChange={handleSearchChange} />
        </div>
        <button
          type="button"
          onClick={() => navigate("/product-order")}
          className={styles.productViewButton}
        >
          <FiGrid className={styles.productViewButtonIcon} />
          Product View
        </button>
      </div>

      <div className={styles.filterPillsContainer}>
        {filterOptions.map((option) => {
          const isActive =
            filter === option.value || (!filter && option.value === undefined);
          return (
            <button
              key={option.label}
              type="button"
              className={`${styles.filterPill} ${isActive ? styles.filterPillActive : ""}`}
              onClick={() => {
                setFilter(option.value);
                setPage(1);
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <div className={styles.ordersTableContainer}>
        {(!orders || orders.length === 0) && (
          <p className={styles.noOrders}>No orders found</p>
        )}

        {orders && orders.length > 0 && (
          <table className={styles.ordersTable}>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>User</th>
                <th>Date</th>
                <th>Status</th>
                <th>Products</th>
                <th>Total Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const shortId = order?._id
                  ? order._id.slice(-4).toUpperCase()
                  : "";
                const status = order?.status || "processing";
                const pillColor =
                  statusColors[status] || statusColors["processing"];
                return (
                  <tr key={order._id}>
                    <td>{shortId ? `HM-${shortId}` : "-"}</td>
                    <td>{order.userName || "-"}</td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>
                      <span
                        className={styles.statusPill}
                        style={{ backgroundColor: pillColor }}
                      >
                        {status === "cancelled" ? "Draft" : status}
                      </span>
                    </td>
                    <td>{order.totalProducts ?? 0}</td>
                    <td>{formatPrice(order.amount)}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.viewDetailsButton}
                        onClick={() => navigate(`/orders/${order._id}`)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.paginationContainer}>
          <button
            type="button"
            className={styles.pageButton}
            disabled={page <= 1}
            onClick={() => page > 1 && setPage(page - 1)}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className={styles.pageButton}
            disabled={page >= totalPages}
            onClick={() => page < totalPages && setPage(page + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
